const DEFAULT_FLAVOR_KEY = 'default'
const FLAVOR_THEME_LINK_ID = 'flavor-theme-css'
const FLAVOR_MANIFEST_LINK_ID = 'flavor-manifest'
const FLAVOR_APPLE_TOUCH_ICON_ID = 'flavor-apple-touch-icon'

const getCurrentHostname = () => {
  if (typeof window === 'undefined') {
    return ''
  }

  return window.location?.hostname || ''
}

const capitalizeDomainLabel = (value = '') => {
  const normalized = String(value).trim().toLowerCase()
  if (!normalized) {
    return ''
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

const extractDomainLabel = (hostname = '') => {
  const normalized = String(hostname)
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/^www\./, '')

  if (!normalized) {
    return ''
  }

  const [label] = normalized.split('.').filter(Boolean)
  return label || ''
}

const socialIconMap = {
  facebook: 'bi bi-facebook',
  instagram: 'bi bi-instagram',
  linkedin: 'bi bi-linkedin',
  youtube: 'bi bi-youtube',
  twitter: 'bi bi-twitter-x',
  x: 'bi bi-twitter-x'
}

const DEFAULT_FLAVOR = {
  key: DEFAULT_FLAVOR_KEY,
  slug: '',
  domains: [],
  displayName: 'PG Booking',
  domainHeaderName: '',
  appName: 'GOPGMS',
  metaTitle: 'Find & Book PG, Hostel Accommodation Online',
  metaDescription:
    'Find and book verified PG and hostel accommodation online. Affordable rooms with modern amenities and transparent pricing.',
  logos: {
    header: '/assets/flavors/default/images/logo-header.png',
    headerMobile: '/assets/flavors/default/images/logo-header-mobile.svg',
    footer: '/assets/flavors/default/images/logo-footer.svg',
    menu: '/assets/flavors/default/images/logo-menu.svg',
    favicon: '/assets/flavors/default/images/favicon.svg'
  },
  contact: {
    whatsapp: {
      label: 'WhatsApp',
      value: '',
      display: ''
    },
    email: {
      label: 'Mail Support',
      value: ''
    },
    phone: {
      label: 'Phone Support',
      value: '',
      display: ''
    }
  },
  socialLinks: [
    { platform: 'facebook', href: 'https://www.facebook.com/', iconClass: socialIconMap.facebook },
    { platform: 'instagram', href: 'https://www.instagram.com/', iconClass: socialIconMap.instagram },
    { platform: 'linkedin', href: 'https://www.linkedin.com/', iconClass: socialIconMap.linkedin },
    { platform: 'youtube', href: 'https://www.youtube.com/', iconClass: socialIconMap.youtube }
  ],
  themeCss: '/assets/flavors/default/theme.css',
  theme: {
    primary: '#1781fe',
    secondary: '#0ea9d0'
  },
  themeColor: '#1781fe',
  ogImage: '/assets/flavors/default/images/og-image.png',
  appleTouchIcon: '/assets/flavors/default/images/apple-touch-icon.png',
  manifest: {
    name: 'PG Booking',
    shortName: 'PG Booking',
    description: 'Find and book verified PG and hostel accommodation online.',
    backgroundColor: '#ffffff',
    themeColor: '#1781fe',
    display: 'standalone',
    startUrl: '/'
  },
  seo: {
    keywords: 'PG, paying guest, hostel, accommodation, booking',
    canonicalBase: ''
  }
}

const FLAVORS = {
  sanwariapghouse: {
    key: 'sanwariapghouse',
    slug: 'sanwariapghouse',
    domains: ['sanwariapghouse.com', 'sanwaria.com'],
    displayName: 'Sanwaria PG House',
    domainHeaderName: 'Sanwariapghouse',
    appName: 'SANWARIAPGHOUSE',
    metaTitle: 'Sanwaria PG House - Best PG in Sarita Vihar, Delhi',
    metaDescription:
      'Find the best paying guest accommodations in Sarita Vihar, Delhi. Affordable rooms for students and working professionals with modern amenities.',
    logos: {
      header: '/assets/flavors/sanwariapghouse/images/logo-header.png',
      headerMobile: '/assets/flavors/sanwariapghouse/images/logo-header-mobile.png',
      footer: '/assets/flavors/sanwariapghouse/images/logo-footer.png',
      menu: '/assets/flavors/sanwariapghouse/images/logo-menu.png',
      favicon: '/assets/flavors/sanwariapghouse/images/favicon.png'
    },
    contact: {
      whatsapp: {
        label: 'WhatsApp',
        value: '91345533865',
        display: '+91 345 533 865'
      },
      email: {
        label: 'Mail Support',
        value: 'info@sanwariapg.com'
      },
      phone: {
        label: 'Phone Support',
        value: '91345533865',
        display: '+91 345 533 865'
      }
    },
    socialLinks: [
      { platform: 'facebook', href: 'https://www.facebook.com/gopgmsapp/', iconClass: socialIconMap.facebook },
      { platform: 'instagram', href: 'https://www.instagram.com/gopgmsofficial/', iconClass: socialIconMap.instagram },
      { platform: 'youtube', href: 'https://www.youtube.com/channel/UCGiqWhKKhR3RE0Gz9T45K3Q', iconClass: socialIconMap.youtube }
    ],
    themeCss: '/assets/flavors/sanwariapghouse/theme.css',
    theme: {
      primary: '#23b5ee',
      secondary: '#1c536c'
    },
    themeColor: '#23b5ee',
    ogImage: '/assets/flavors/sanwariapghouse/images/og-image.png',
    appleTouchIcon: '/assets/flavors/sanwariapghouse/images/apple-touch-icon.png',
    manifest: {
      name: 'Sanwaria PG House',
      shortName: 'Sanwaria PG',
      description: 'Find the best paying guest accommodations in Sarita Vihar, Delhi.',
      backgroundColor: '#ffffff',
      themeColor: '#23b5ee',
      display: 'standalone',
      startUrl: '/'
    },
    seo: {
      keywords: 'PG, paying guest, hostel, accommodation, Sarita Vihar, Delhi',
      canonicalBase: 'https://sanwariapghouse.com'
    },
    managementAppUrl: 'https://app.sanwariapghouse.com'
  },
  bookabed: {
    key: 'bookabed',
    slug: 'bookabed',
    domains: ['bookabed.in', 'booking.gopgms.com'],
    displayName: 'Book A Bed',
    domainHeaderName: 'Bookabed',
    appName: 'BOOKABED',
    metaTitle: 'Book A Bed - Find Your PG & Hostel',
    metaDescription:
      'Bookabed.in helps you find and book verified PG and hostel stays with transparent pricing and modern amenities.',
    logos: {
      header: '/assets/flavors/bookabed/images/logo-header.png',
      headerMobile: '/assets/flavors/bookabed/images/logo-header-mobile.png',
      footer: '/assets/flavors/bookabed/images/logo-footer.png',
      menu: '/assets/flavors/bookabed/images/logo-menu.png',
      favicon: '/assets/flavors/bookabed/images/favicon.png'
    },
    contact: {
      whatsapp: {
        label: 'WhatsApp',
        value: '918010942551',
        display: '+91 80109 42551'
      },
      email: {
        label: 'Mail Support',
        value: 'gopgms9@gmail.com'
      },
      phone: {
        label: 'Phone Support',
        value: '918010942551',
        display: '+91 80109 42551'
      }
    },
    socialLinks: [
      { platform: 'facebook', href: 'https://www.facebook.com/', iconClass: socialIconMap.facebook },
      { platform: 'instagram', href: 'https://www.instagram.com/', iconClass: socialIconMap.instagram },
      { platform: 'linkedin', href: 'https://www.linkedin.com/', iconClass: socialIconMap.linkedin },
      { platform: 'youtube', href: 'https://www.youtube.com/', iconClass: socialIconMap.youtube }
    ],
    themeCss: '/assets/flavors/bookabed/theme.css',
    theme: {
      primary: '#0f4e9f',
      secondary: '#f5861f'
    },
    themeColor: '#0f4e9f',
    ogImage: '/assets/flavors/bookabed/images/og-image.jpg',
    appleTouchIcon: '/assets/flavors/bookabed/images/apple-touch-icon.png',
    manifest: {
      name: 'Book A Bed',
      shortName: 'BookABed',
      description: 'Find and book verified PG and hostel stays with transparent pricing.',
      backgroundColor: '#ffffff',
      themeColor: '#0f4e9f',
      display: 'standalone',
      startUrl: '/'
    },
    seo: {
      keywords: 'PG, hostel, paying guest, accommodation, India, booking',
      canonicalBase: 'https://bookabed.in'
    },
    managementAppUrl: 'https://app.gopgms.com'
  },
  urbanstays: {
    key: 'urbanstays',
    slug: 'urban-stays',
    domains: ['urbanstays.in'],
    displayName: 'Urban Stays',
    domainHeaderName: 'urbanstays',
    appName: 'URBANSTAYS',
    metaTitle: 'Urban Stays — Smart | Healthy | Hygiene',
    metaDescription:
      'Smart, healthy & hygienic PG accommodation in Kadubeesanahalli, Bangalore. Book your stay at Urban Stays.',
    logos: {
      header: '/assets/flavors/urbanstays/images/logo-header.png',
      headerMobile: '/assets/flavors/urbanstays/images/logo-header-mobile.png',
      footer: '/assets/flavors/urbanstays/images/logo-footer.png',
      menu: '/assets/flavors/urbanstays/images/logo-menu.png',
      favicon: '/assets/flavors/urbanstays/images/favicon.svg'
    },
    contact: {
      whatsapp: {
        label: 'WhatsApp',
        value: '917090834443',
        display: '70908 34443'
      },
      email: {
        label: 'Mail Support',
        value: 'info.urbanstays@gmail.com'
      },
      phone: {
        label: 'Phone Support',
        value: '917090834443',
        display: '+91 70908 34443'
      },
      phoneAlt: {
        label: 'Alternate Phone',
        value: '917090834442',
        display: '+91 70908 34442'
      }
    },
    socialLinks: [
      { platform: 'facebook', href: 'https://www.facebook.com/', iconClass: 'bi bi-facebook' },
      { platform: 'instagram', href: 'https://www.instagram.com/', iconClass: 'bi bi-instagram' },
      { platform: 'linkedin', href: 'https://www.linkedin.com/', iconClass: 'bi bi-linkedin' },
      { platform: 'youtube', href: 'https://www.youtube.com/', iconClass: 'bi bi-youtube' }
    ],
    themeCss: '/assets/flavors/urbanstays/theme.css',
    theme: {
      primary: '#D4B96B',
      secondary: '#3D2B1F'
    },
    themeColor: '#D4B96B',
    ogImage: '/assets/flavors/urbanstays/images/og-image.png',
    appleTouchIcon: '/assets/flavors/urbanstays/images/apple-touch-icon.png',
    manifest: {
      name: 'Urban Stays',
      shortName: 'Urban',
      description: 'Smart, healthy & hygienic PG accommodation in Kadubeesanahalli, Bangalore. Book your stay at Urban Stays.',
      backgroundColor: '#ffffff',
      themeColor: '#D4B96B',
      display: 'standalone',
      startUrl: '/'
    },
    seo: {
      keywords: 'PG, hostel, paying guest, accommodation, booking',
      canonicalBase: 'https://urbanstays.in'
    }
  }
}

const matchFlavorKey = (hostname) => {
  if (!hostname) {
    return DEFAULT_FLAVOR_KEY
  }

  // Allow overriding flavor via env variable in development
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_FLAVOR) {
    const devKey = import.meta.env.VITE_DEV_FLAVOR.toLowerCase()
    if (FLAVORS[devKey]) {
      return devKey
    }
  }

  const normalized = hostname.toLowerCase()

  const matchedFlavor = Object.values(FLAVORS).find((flavor) => {
    const matchers = [
      flavor?.key,
      ...(Array.isArray(flavor?.domains) ? flavor.domains : [])
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean)

    return matchers.some((matcher) => normalized.includes(matcher))
  })

  if (matchedFlavor?.key) {
    return matchedFlavor.key
  }

  return DEFAULT_FLAVOR_KEY
}

const mergeFlavorWithDefault = (flavorKey) => {
  const flavor = FLAVORS[flavorKey] ?? {}

  return {
    ...DEFAULT_FLAVOR,
    ...flavor,
    key: flavor.key ?? flavorKey ?? DEFAULT_FLAVOR.key,
    logos: {
      ...DEFAULT_FLAVOR.logos,
      ...(flavor.logos || {})
    },
    contact: {
      ...DEFAULT_FLAVOR.contact,
      ...(flavor.contact || {}),
      whatsapp: {
        ...DEFAULT_FLAVOR.contact.whatsapp,
        ...(flavor.contact?.whatsapp || {})
      },
      email: {
        ...DEFAULT_FLAVOR.contact.email,
        ...(flavor.contact?.email || {})
      },
      phone: {
        ...DEFAULT_FLAVOR.contact.phone,
        ...(flavor.contact?.phone || {})
      }
    },
    socialLinks: flavor.socialLinks ?? DEFAULT_FLAVOR.socialLinks,
    theme: {
      ...DEFAULT_FLAVOR.theme,
      ...(flavor.theme || {})
    },
    themeCss: flavor.themeCss ?? DEFAULT_FLAVOR.themeCss,
    themeColor: flavor.themeColor ?? DEFAULT_FLAVOR.themeColor,
    ogImage: flavor.ogImage ?? DEFAULT_FLAVOR.ogImage,
    appleTouchIcon: flavor.appleTouchIcon ?? DEFAULT_FLAVOR.appleTouchIcon,
    manifest: {
      ...DEFAULT_FLAVOR.manifest,
      ...(flavor.manifest || {})
    },
    seo: {
      ...DEFAULT_FLAVOR.seo,
      ...(flavor.seo || {})
    }
  }
}

const upsertThemeLink = (href) => {
  if (!document?.head) {
    return
  }

  const themeHref = href || DEFAULT_FLAVOR.themeCss
  let link = document.getElementById(FLAVOR_THEME_LINK_ID)

  if (!link) {
    link = document.createElement('link')
    link.id = FLAVOR_THEME_LINK_ID
    link.rel = 'stylesheet'
    document.head.appendChild(link)
  }

  if (link.getAttribute('href') !== themeHref) {
    link.setAttribute('href', themeHref)
  }
}

const upsertAppleTouchIcon = (href) => {
  if (!document?.head || !href) {
    return
  }

  let link = document.getElementById(FLAVOR_APPLE_TOUCH_ICON_ID)

  if (!link) {
    link = document.createElement('link')
    link.id = FLAVOR_APPLE_TOUCH_ICON_ID
    link.rel = 'apple-touch-icon'
    document.head.appendChild(link)
  }

  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href)
  }
}

const upsertManifest = (manifest, flavorKey) => {
  if (!document?.head || !manifest) {
    return
  }

  const manifestData = {
    name: manifest.name,
    short_name: manifest.shortName,
    description: manifest.description,
    start_url: manifest.startUrl || '/',
    display: manifest.display || 'standalone',
    background_color: manifest.backgroundColor || '#ffffff',
    theme_color: manifest.themeColor,
    icons: [
      {
        src: `/assets/flavors/${flavorKey === 'sanwariapghouse' ? 'default' : flavorKey}/images/icon-192.png`,
        sizes: '192x192',
        type: 'image/png'
      },
      {
        src: `/assets/flavors/${flavorKey === 'sanwariapghouse' ? 'default' : flavorKey}/images/icon-512.png`,
        sizes: '512x512',
        type: 'image/png'
      }
    ]
  }

  const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/json' })
  const manifestUrl = URL.createObjectURL(blob)

  let link = document.getElementById(FLAVOR_MANIFEST_LINK_ID)

  if (!link) {
    link = document.createElement('link')
    link.id = FLAVOR_MANIFEST_LINK_ID
    link.rel = 'manifest'
    document.head.appendChild(link)
  }

  // Revoke old blob URL to prevent memory leak
  const oldHref = link.getAttribute('href')
  if (oldHref && oldHref.startsWith('blob:')) {
    URL.revokeObjectURL(oldHref)
  }

  link.setAttribute('href', manifestUrl)
}

const upsertOrCreateMeta = (selector, attrName, value) => {
  let tag = document.querySelector(selector)

  if (!tag) {
    tag = document.createElement('meta')
    // Parse selector to set attributes
    if (selector.includes('property=')) {
      const propMatch = selector.match(/property="([^"]+)"/)
      if (propMatch) tag.setAttribute('property', propMatch[1])
    } else if (selector.includes('name=')) {
      const nameMatch = selector.match(/name="([^"]+)"/)
      if (nameMatch) tag.setAttribute('name', nameMatch[1])
    }
    document.head.appendChild(tag)
  }

  tag.setAttribute(attrName, value)
}

export const resolveFlavor = (hostname = window.location.hostname) => {
  const key = matchFlavorKey(hostname)
  return mergeFlavorWithDefault(key)
}

export const resolveDomainHeaderValue = (hostname = getCurrentHostname()) => {
  const flavor = resolveFlavor(hostname)
  const configuredHeader = String(flavor?.domainHeaderName || '').trim()

  if (configuredHeader) {
    return configuredHeader
  }

  const domainLabel = extractDomainLabel(hostname)
  if (domainLabel) {
    return capitalizeDomainLabel(domainLabel)
  }

  return capitalizeDomainLabel(DEFAULT_FLAVOR_KEY)
}

export const applyFlavor = (flavor) => {
  if (!flavor) {
    return
  }

  // Set document title
  document.title = flavor.metaTitle

  // Set meta description
  const metaDescription = document.querySelector('meta[name="description"]')
  if (metaDescription) {
    metaDescription.setAttribute('content', flavor.metaDescription)
  }

  // Helper to set meta tags
  const setMetaTag = (selector, value) => {
    const tag = document.querySelector(selector)
    if (!tag) {
      return
    }
    tag.setAttribute('content', value)
  }

  // Open Graph meta tags
  setMetaTag('meta[property="og:title"]', flavor.metaTitle)
  setMetaTag('meta[property="og:description"]', flavor.metaDescription)
  setMetaTag('meta[property="og:site_name"]', flavor.displayName)

  // Twitter meta tags
  setMetaTag('meta[name="twitter:title"]', flavor.metaTitle)
  setMetaTag('meta[name="twitter:description"]', flavor.metaDescription)

  // OG Image & Twitter Image
  if (flavor.ogImage) {
    upsertOrCreateMeta('meta[property="og:image"]', 'content', flavor.ogImage)
    upsertOrCreateMeta('meta[name="twitter:image"]', 'content', flavor.ogImage)
  }

  // Theme color meta tag (for mobile browser chrome)
  if (flavor.themeColor) {
    upsertOrCreateMeta('meta[name="theme-color"]', 'content', flavor.themeColor)
  }

  // SEO keywords
  if (flavor.seo?.keywords) {
    upsertOrCreateMeta('meta[name="keywords"]', 'content', flavor.seo.keywords)
  }

  // Favicon
  const faviconHref = flavor.logos.favicon
  if (faviconHref) {
    // Extract extension from URL path (ignore query params)
    const pathname = faviconHref.split('?')[0]
    const ext = pathname.split('.').pop().toLowerCase()
    const typeMap = { svg: 'image/svg+xml', png: 'image/png', ico: 'image/x-icon' }
    const mimeType = typeMap[ext] || 'image/png'

    // Replace the favicon link element entirely — browsers ignore href-only changes
    const oldFavicon = document.getElementById('app-favicon') || document.querySelector('link[rel="icon"]')
    if (oldFavicon) oldFavicon.remove()

    const newFavicon = document.createElement('link')
    newFavicon.id = 'app-favicon'
    newFavicon.rel = 'icon'
    newFavicon.type = mimeType
    newFavicon.href = faviconHref
    document.head.appendChild(newFavicon)
  }

  // Theme CSS
  upsertThemeLink(flavor.themeCss)

  // Apple Touch Icon
  if (flavor.appleTouchIcon) {
    upsertAppleTouchIcon(flavor.appleTouchIcon)
  }

  // PWA Manifest
  if (flavor.manifest) {
    upsertManifest(flavor.manifest, flavor.key)
  }

  // Set data attribute and CSS variables
  document.body.dataset.flavor = flavor.key
  document.documentElement.style.setProperty('--flavor-primary', flavor.theme.primary)
  document.documentElement.style.setProperty('--flavor-secondary', flavor.theme.secondary)

  // Keep backward compatibility with --brand-* variables
  document.documentElement.style.setProperty('--brand-primary', flavor.theme.primary)
  document.documentElement.style.setProperty('--brand-secondary', flavor.theme.secondary)
}

// ---------------------------------------------------------------------------
// API flavor merge — takes the account API response and merges its flavor
// data into the local flavor object (mutating in place).
// ---------------------------------------------------------------------------
const isUsableUrl = (v) => typeof v === 'string' && (v.startsWith('http') || v.startsWith('/'))

export const mergeApiFlavor = (localFlavor, accountData) => {
  const apiFlavor = accountData?.flavor
  if (!apiFlavor) return localFlavor

  // ── Core identity ──
  if (apiFlavor.key) localFlavor.key = apiFlavor.key
  if (apiFlavor.displayName) localFlavor.displayName = apiFlavor.displayName
  if (apiFlavor.appName) localFlavor.appName = apiFlavor.appName
  if (apiFlavor.domains?.length) localFlavor.domains = apiFlavor.domains
  if (accountData.accountSlug) localFlavor.slug = accountData.accountSlug

  // ── Logos ──
  // API may return UUIDs (not usable as src) or full URLs.
  // accountData.logo is always a pre-signed S3 URL when available.
  const topLogo = accountData.logo
  if (isUsableUrl(topLogo)) {
    localFlavor.logos.header = topLogo
    localFlavor.logos.footer = topLogo
    localFlavor.logos.menu = topLogo
    localFlavor.logos.headerMobile = topLogo
    localFlavor.logos.favicon = topLogo
  }
  // Override with specific logo URLs if the API returned real URLs
  if (isUsableUrl(apiFlavor.logos?.header)) localFlavor.logos.header = apiFlavor.logos.header
  if (isUsableUrl(apiFlavor.logos?.footer)) localFlavor.logos.footer = apiFlavor.logos.footer
  if (isUsableUrl(apiFlavor.logos?.favicon)) localFlavor.logos.favicon = apiFlavor.logos.favicon
  if (isUsableUrl(apiFlavor.logos?.headerMobile)) localFlavor.logos.headerMobile = apiFlavor.logos.headerMobile
  if (isUsableUrl(apiFlavor.logos?.ogImage)) localFlavor.ogImage = apiFlavor.logos.ogImage

  // ── Theme ──
  if (apiFlavor.theme?.primaryColor) {
    localFlavor.theme.primary = apiFlavor.theme.primaryColor
    localFlavor.themeColor = apiFlavor.theme.primaryColor
  }
  if (apiFlavor.theme?.secondaryColor) {
    localFlavor.theme.secondary = apiFlavor.theme.secondaryColor
  }

  // ── SEO / Meta ──
  if (apiFlavor.seo?.title) localFlavor.metaTitle = apiFlavor.seo.title
  if (apiFlavor.seo?.description) localFlavor.metaDescription = apiFlavor.seo.description
  if (apiFlavor.seo?.keywords) {
    localFlavor.seo = localFlavor.seo || {}
    localFlavor.seo.keywords = apiFlavor.seo.keywords
  }

  // ── Contact ──
  if (apiFlavor.contact) {
    const c = apiFlavor.contact
    if (c.whatsApp) {
      localFlavor.contact.whatsapp = {
        label: 'WhatsApp', value: c.whatsApp.replace(/[^\d+]/g, ''), display: c.whatsApp
      }
    }
    if (c.email) {
      localFlavor.contact.email = { label: 'Mail Support', value: c.email }
    }
    if (c.phone) {
      localFlavor.contact.phone = {
        label: 'Phone Support', value: c.phone.replace(/[^\d+]/g, ''), display: c.phone
      }
    }
    if (c.address) localFlavor.contact.address = c.address
  }

  // ── Social links ──
  if (apiFlavor.social) {
    const links = []
    for (const [platform, href] of Object.entries(apiFlavor.social)) {
      if (href && socialIconMap[platform]) {
        links.push({ platform, href, iconClass: socialIconMap[platform] })
      }
    }
    if (links.length) localFlavor.socialLinks = links
  }

  // ── Manifest ──
  if (apiFlavor.manifest) {
    localFlavor.manifest = { ...localFlavor.manifest }
    if (apiFlavor.manifest.name) localFlavor.manifest.name = apiFlavor.manifest.name
    if (apiFlavor.manifest.shortName) localFlavor.manifest.shortName = apiFlavor.manifest.shortName
    if (apiFlavor.manifest.themeColor) localFlavor.manifest.themeColor = apiFlavor.manifest.themeColor
    if (apiFlavor.manifest.backgroundColor) localFlavor.manifest.backgroundColor = apiFlavor.manifest.backgroundColor
  }

  // ── Management app URL ──
  if (apiFlavor.managementAppUrl) localFlavor.managementAppUrl = apiFlavor.managementAppUrl

  // ── Footer data (raw API shape, consumed by Footer.vue) ──
  if (apiFlavor.footer) localFlavor._apiFooter = apiFlavor.footer

  // ── Hero media ──
  if (apiFlavor.heroMedia) localFlavor._apiHeroMedia = apiFlavor.heroMedia

  return localFlavor
}

export { FLAVORS, DEFAULT_FLAVOR_KEY, extractDomainLabel, socialIconMap }
